import { Page, View, Document } from "@react-pdf/renderer";
import {
  FormHeading,
  PageContent,
  Flex,
  FlexRow,
  H2,
  H3,
  H4,
  P,
  CheckInput,
  TextInput,
  PageHeading,
  TextAreaInput,
} from "./components";
import {
  calculateAge,
  formatDateToDD_MMM_YYYY,
  formatPhone,
} from "../../helpers/functions";
import {
  biologicalSexOptions,
  countryOptions,
  genderOptions,
  relationshipTypeOptions,
  relationshipTypeThirdStepOptions,
  stateOptions,
} from "../patient/create/options";
import { trimesterEnum } from "../patient/clinical-services/service-steps/pre-assessment/data";

export const PhysicianReferralForm = ({ patient, initialAssessmentForm }) => {
  if (!patient || !initialAssessmentForm) {
    alert("no patient or initialAssessmentForm");
    return null;
  }

  const serviceName = "Allergic Rhinitis";

  const caregiver = patient.caregiver?.[0] || null;
  const careProvider = patient.careProvider?.[0] || null;

  const formValues = {
    serviceDate: initialAssessmentForm.preAssessment.serviceDate
      ? formatDateToDD_MMM_YYYY(initialAssessmentForm.preAssessment.serviceDate)
      : "",
    to: careProvider
      ? (
          (careProvider.firstName || "") +
          (careProvider.middleName ? ` ${careProvider.middleName}` : "") +
          (careProvider.lastName ? ` ${careProvider.lastName}` : "")
        ).trim()
      : "",
    patientInformation: {
      patientName: (
        (patient.firstName || "") +
        (patient.middleName ? ` ${patient.middleName}` : "") +
        (patient.lastName ? ` ${patient.lastName}` : "")
      ).trim(),
      healthCardNumber: patient.provincialInsurance?.cardNumber,
      versionCode: patient.provincialInsurance?.versionCode,
      expiryDate: patient.provincialInsurance?.expiryDate,
      dateOfBirth: patient.dateOfBirth
        ? formatDateToDD_MMM_YYYY(patient.dateOfBirth)
        : "",
      age: patient.dateOfBirth ? calculateAge(patient.dateOfBirth) : "",
      gender: genderOptions.find((g) => g.value === patient.gender)?.label,
      biologicalSex: biologicalSexOptions.find(
        (g) => g.value === patient.biologicalSex
      )?.label,
    },
    patientContactInformation: {
      noPhoneInformation: !patient.contactInfo,
      primaryPhone: patient.contactInfo?.primaryPhone
        ? `+${patient.contactInfo.primaryPhoneCountryCode} ${formatPhone(
            patient.contactInfo.primaryPhone
          )}`
        : "",
      secondaryPhone: patient.contactInfo?.secondaryPhone
        ? `+${patient.contactInfo.secondaryPhoneCountryCode} ${formatPhone(
            patient.contactInfo.secondaryPhone
          )}`
        : "",
      email: patient.contactInfo?.email,
      address: patient.contactInfo?.address,
      unitNumber: patient.contactInfo?.unitNumber,
      poBox: patient.contactInfo?.poBox,
      country: countryOptions.find(
        (c) => c.value === patient.contactInfo?.country
      )?.label,
      province: stateOptions.find(
        (s) => s.value === patient.contactInfo?.province
      )?.label,
      city: patient.contactInfo?.city,
      postalCode: patient.contactInfo?.postalCode,
    },
    alternateCaregiverAgentInformation: {
      noAlternateCaregiver: !caregiver,
      alternateCaregiverAgentName: caregiver
        ? (
            (caregiver.firstName || "") +
            (caregiver.middleName ? ` ${caregiver.middleName}` : "") +
            (caregiver.lastName ? ` ${caregiver.lastName}` : "")
          ).trim()
        : "",
      relationship: caregiver
        ? [
            ...relationshipTypeOptions,
            ...relationshipTypeThirdStepOptions,
          ].find((r) => r.value === caregiver.relationship)?.label
        : "",
      primaryPhone: caregiver
        ? caregiver.contactInfo?.primaryPhone
          ? `+${caregiver.contactInfo.primaryPhoneCountryCode} ${formatPhone(
              caregiver.contactInfo.primaryPhone
            )}`
          : ""
        : "",
      secondaryPhone: caregiver
        ? caregiver.contactInfo?.secondaryPhone
          ? `+${caregiver.contactInfo.secondaryPhoneCountryCode} ${formatPhone(
              caregiver.contactInfo.secondaryPhone
            )}`
          : ""
        : "",
      email: caregiver?.contactInfo?.email,
    },
    allergyInformation: {
      noKnownAllergies: !patient.allergyInfo?.allergies?.length,
      hasKnownAllergies: patient.allergyInfo?.allergies?.length > 0,
      allergies:
        patient.allergyInfo?.allergies
          ?.map?.((a) => a.allergyName)
          ?.join(", ") || "",
    },
    consentInformation: {
      informedConsentObtainedBy: {
        patient:
          initialAssessmentForm.preAssessment?.patientAcknowledgeIndicator,
        patientAndCaregiver: false, // TODO: preAssessment.attendees and you will see whether it is patient or alternate caregiver, and then patientAcknowledgeIndicator should be true - Dhruv
        pharmacist:
          initialAssessmentForm.preAssessment?.pharmacistAcknowledgeIndicator,
      },
    },
    reasonsForReferral: {
      primaryPhysicianOrAltHealthcareProvider: false, // TODO: Let's connect about this, hard to type it out - Dhruv
      specialPopulation: {
        pregnant:
          initialAssessmentForm.preAssessment?.pregnancyIndicator === "Yes",
        trimester: trimesterEnum.options.find(
          (t) =>
            t.value === initialAssessmentForm.preAssessment?.pregnancyTrimester
        )?.label,
        breastfeeding:
          initialAssessmentForm.preAssessment?.breastFeedingIndicator === "Yes",
        ageOfInfant: initialAssessmentForm.preAssessment?.expectancyDate
          ? calculateAge(initialAssessmentForm.preAssessment?.expectancyDate)
          : "",
      },
      redFlagSignsAndSymptoms: {
        redFlagSymptoms:
          !!initialAssessmentForm.signsAndSymptoms?.redFlagSymptoms?.length,
        redFlagSymptomsValue:
          initialAssessmentForm.signsAndSymptoms?.redFlagSymptoms?.join?.(", "),
        infectiveRhinitis: false, // TODO: Let's connect about this, hard to type it out - Dhruv
        chronicRhinosinusitis:
          !!initialAssessmentForm.signsAndSymptoms
            ?.chronicRhinosinusitisSymptoms?.length,
      },
      presentingSymptomsAndMinorAilment: {
        noSymptomsMatchMinorAilments: false, // TODO: Let's connect about this, hard to type it out - Dhruv
        noTypicalSignsAndSymptoms: false, // TODO: Let's connect about this, hard to type it out - Dhruv
      },
      redFlagMedicalConditions: {
        // Dhruv: Let's connect about these, hard to type it out
        otitisMedia: false, // TODO
        chronicRecurrentSinusitis: false, // TODO
        uncontrolledThyroid: false, // TODO
        uncontrolledAsthma: false, // TODO
      },
      drugInducedAllergicRhinitis: {
        prescriptionMedications:
          !!initialAssessmentForm.clinicalAssessment?.anyPrescriptionMedication,
        prescriptionMedicationsValue:
          initialAssessmentForm.clinicalAssessment?.otherPrescriptionMedications?.join?.(
            ", "
          ) || "",
        oralContraceptives:
          !!initialAssessmentForm.clinicalAssessment?.oralContraceptives,
        nonPrescriptionMedications:
          !!initialAssessmentForm.clinicalAssessment?.nonPrescriptionMedications
            ?.length,
        // Dhruv: Let's connect about this, hard to type it out
        herbalVitamins: false, // TODO
        sampleMedications: false, // TODO
        cocaine: false, // TODO
      },
      differentialAssessment: {
        irritantRhinitis:
          !!initialAssessmentForm.differentialAssessment?.irritantRhinitis
            ?.length,
        irritantRhinitisValue:
          initialAssessmentForm.differentialAssessment?.irritantRhinitis?.join?.(
            ", "
          ) || "",
        occupationalRhinitis:
          !!initialAssessmentForm.differentialAssessment
            ?.occupationalRhinitisIndicator,
        structuralOrMechanical:
          !!initialAssessmentForm.differentialAssessment
            ?.structuralOrMechanicalConditions?.length,
        systemicConditions:
          !!initialAssessmentForm.differentialAssessment?.systemicConditions
            ?.length,
      },
      interventions: {
        suggestedToDiscontinueMedication: false, // TODO: unchecked for now - Dhruv
        suggestedToChangeMedication: false, // TODO: unchecked for now - Dhruv
      },
    },
    prescribingPharmacistInformation: {
      // Dhruv: we would not have this right now, ask Vesna if we can put placeholder info
      name: "", // TODO
      licenseNumber: "", // TODO
      signature: "", // TODO
    },
    pharmacyInformation: {
      // Dhruv: we would not have this right now, ask Vesna if we can put placeholder info
      name: "", // TODO
      storeNumber: "", // TODO
      phone: "", // TODO
      fax: "", // TODO
      signature: "", // TODO
      address: "", // TODO
    },
  };

  return (
    <Document>
      <Page
        size="A4"
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 30,
        }}
      >
        <FormHeading
          title="Primary Provider Referral Form"
          pharmacyName="Pharmacy Name/Logo"
        />
        <PageContent>
          <Flex gap={16}>
            <Flex gap={8}>
              <H4>Service Date: {formValues.serviceDate}</H4>
              <H4>To: {formValues.to}</H4>
            </Flex>
            <Flex gap={4}>
              <H4>After an assessment with the patient for : {serviceName}</H4>
              <P>A referral was recommended, with reasons included below.</P>
              <P>
                This is for your information to keep your records for this
                patient up to date.
              </P>
            </Flex>
          </Flex>
          <Flex gap={16}>
            <H2 filled>Patient Information</H2>
            <Flex gap={8}>
              <TextInput
                label="Patient Name"
                value={formValues.patientInformation.patientName}
                required
              />
              <FlexRow gap={12}>
                <TextInput
                  label="Health Card Number"
                  value={formValues.patientInformation.healthCardNumber}
                />
                <FlexRow gap={12}>
                  <TextInput
                    label="Version Code"
                    value={formValues.patientInformation.versionCode}
                  />
                  <TextInput
                    label="Expiry Date"
                    value={formValues.patientInformation.expiryDate}
                  />
                </FlexRow>
              </FlexRow>
              <FlexRow gap={12}>
                <TextInput
                  label="Date Of Birth"
                  required
                  value={formValues.patientInformation.dateOfBirth}
                />
                <TextInput
                  label="Age"
                  required
                  value={formValues.patientInformation.age}
                />
              </FlexRow>
              <FlexRow gap={12}>
                <TextInput
                  label="Gender (Optional)"
                  value={formValues.patientInformation.gender}
                />
                <TextInput
                  label="Biological Sex (Optional)"
                  value={formValues.patientInformation.biologicalSex}
                />
              </FlexRow>
            </Flex>
          </Flex>
          <Flex gap={16}>
            <H2 filled>Patient Contact Information</H2>
            <Flex gap={8}>
              <Flex gap={4}>
                <CheckInput
                  checked={
                    formValues.patientContactInformation.noPhoneInformation
                  }
                  label="No Phone Information"
                />
                <FlexRow gap={12}>
                  <TextInput
                    label="Primary Phone Number"
                    required
                    value={formValues.patientContactInformation.primaryPhone}
                  />
                  <TextInput
                    label="Secondary Phone"
                    value={formValues.patientContactInformation.secondaryPhone}
                  />
                </FlexRow>
              </Flex>
              <TextInput
                label="Email Address"
                value={formValues.patientContactInformation.email}
              />
            </Flex>
          </Flex>
        </PageContent>
      </Page>

      <Page
        size="A4"
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 30,
        }}
      >
        <PageHeading pharmacyName="Pharmacy Name/Logo" />
        <PageContent>
          <Flex gap={16}>
            <Flex gap={8}>
              <TextInput
                label="Address (Street Number and Name)"
                required
                value={formValues.patientContactInformation.address}
              />
              <FlexRow gap={12}>
                <TextInput
                  label="Unit Number"
                  value={formValues.patientContactInformation.unitNumber}
                />
                <TextInput
                  label="PO Box"
                  value={formValues.patientContactInformation.poBox}
                />
              </FlexRow>
              <FlexRow gap={12}>
                <TextInput
                  label="Country"
                  required
                  value={formValues.patientContactInformation.country}
                />
                <TextInput
                  label="Province"
                  required
                  value={formValues.patientContactInformation.province}
                />
              </FlexRow>
              <FlexRow gap={12}>
                <TextInput
                  label="City/Town"
                  required
                  value={formValues.patientContactInformation.city}
                />
                <TextInput
                  label="Postal Code"
                  required
                  value={formValues.patientContactInformation.postalCode}
                />
              </FlexRow>
            </Flex>
          </Flex>
          <Flex gap={16}>
            <H2 filled>Alternate Caregiver / Agent Information (Optional)</H2>
            <Flex gap={8}>
              <CheckInput
                checked={
                  formValues.alternateCaregiverAgentInformation
                    .noAlternateCaregiver
                }
                label="No Alternate Caregiver"
              />
              <TextInput
                label="Alternate Caregiver / Agent Name"
                required
                value={
                  formValues.alternateCaregiverAgentInformation
                    .alternateCaregiverAgentName
                }
              />
              <FlexRow gap={12}>
                <TextInput
                  label="Relationship"
                  required
                  value={
                    formValues.alternateCaregiverAgentInformation.relationship
                  }
                />
                <View></View>
              </FlexRow>
              <FlexRow gap={12}>
                <TextInput
                  label="Primary Phone Number"
                  required
                  value={
                    formValues.alternateCaregiverAgentInformation.primaryPhone
                  }
                />
                <TextInput
                  label="Secondary Phone"
                  value={
                    formValues.alternateCaregiverAgentInformation.secondaryPhone
                  }
                />
              </FlexRow>
              <TextInput
                label="Email Address"
                value={formValues.alternateCaregiverAgentInformation.email}
              />
            </Flex>
          </Flex>
          <Flex gap={16}>
            <H2 filled>Allergy Information</H2>
            <Flex gap={8}>
              <CheckInput
                checked={formValues.allergyInformation.noKnownAllergies}
                label="No Known Allergies"
              />
              <Flex gap={4}>
                <CheckInput
                  checked={formValues.allergyInformation.hasKnownAllergies}
                  label="Has Known Allergies"
                />
                <View style={{ paddingLeft: 22 }}>
                  <TextInput value={formValues.allergyInformation.allergies} />
                </View>
              </Flex>
            </Flex>
          </Flex>
        </PageContent>
      </Page>

      <Page
        size="A4"
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 30,
        }}
      >
        <PageHeading pharmacyName="Pharmacy Name/Logo" />
        <PageContent>
          <Flex gap={16}>
            <H2 filled>Consent Information</H2>
            <Flex gap={8}>
              <H3>Informed Consent Obtained by:</H3>
              <CheckInput
                checked={
                  formValues.consentInformation.informedConsentObtainedBy
                    .patient
                }
                label="Patient"
              />
              <CheckInput
                checked={
                  formValues.consentInformation.informedConsentObtainedBy
                    .patientAndCaregiver
                }
                label="Patient & Alternate Caregiver"
              />
              <CheckInput
                checked={
                  formValues.consentInformation.informedConsentObtainedBy
                    .pharmacist
                }
                label="Pharmacist Acknowledgement/Consent"
              />
            </Flex>
          </Flex>
          <Flex gap={16}>
            <H2 filled>Reason(s) for Referral</H2>
            <CheckInput
              checked={
                formValues.reasonsForReferral
                  .primaryPhysicianOrAltHealthcareProvider
              }
              label="Referral to Primary Provider or Alternate Healthcare Provider"
            />
          </Flex>
          <Flex gap={16}>
            <H3>Special Population</H3>
            <Flex gap={8}>
              <FlexRow gap={10} style={{ width: "100%" }}>
                <CheckInput
                  checked={
                    formValues.reasonsForReferral.specialPopulation.pregnant
                  }
                  label="Pregnant - Trimester:"
                />
                <View
                  style={{
                    borderBottom: "1px solid #D0D0D0",
                    minWidth: 100,
                  }}
                >
                  <P>
                    {formValues.reasonsForReferral.specialPopulation.trimester}
                  </P>
                </View>
              </FlexRow>
              <FlexRow gap={10} style={{ width: "100%" }}>
                <CheckInput
                  checked={
                    formValues.reasonsForReferral.specialPopulation
                      .breastfeeding
                  }
                  label="Breastfeeding - Age of Infant:"
                />
                <View
                  style={{
                    borderBottom: "1px solid #D0D0D0",
                    minWidth: 100,
                  }}
                >
                  <P>
                    {
                      formValues.reasonsForReferral.specialPopulation
                        .ageOfInfant
                    }
                  </P>
                </View>
              </FlexRow>
            </Flex>
          </Flex>
          <Flex gap={16}>
            <H3>Red Flag Signs & Symptoms</H3>
            <Flex gap={8}>
              <Flex gap={4}>
                <CheckInput
                  checked={
                    formValues.reasonsForReferral.redFlagSignsAndSymptoms
                      .redFlagSymptoms
                  }
                  label="Red Flag Symptoms"
                />
                <View style={{ paddingLeft: 22 }}>
                  <TextInput
                    value={
                      formValues.reasonsForReferral.redFlagSignsAndSymptoms
                        .redFlagSymptomsValue
                    }
                  />
                </View>
              </Flex>
              <CheckInput
                checked={
                  formValues.reasonsForReferral.redFlagSignsAndSymptoms
                    .infectiveRhinitis
                }
                label="Infective Rhinitis"
              />
              <CheckInput
                checked={
                  formValues.reasonsForReferral.redFlagSignsAndSymptoms
                    .chronicRhinosinusitis
                }
                label="Chronic Rhinosinusitis"
              />
            </Flex>
          </Flex>
          <Flex gap={16}>
            <H3>Presenting Symptoms & Minor Ailment</H3>
            <Flex gap={8}>
              <CheckInput
                checked={
                  formValues.reasonsForReferral
                    .presentingSymptomsAndMinorAilment
                    .noSymptomsMatchMinorAilments
                }
                label="No symptoms match minor ailments"
              />
              <CheckInput
                checked={
                  formValues.reasonsForReferral
                    .presentingSymptomsAndMinorAilment.noTypicalSignsAndSymptoms
                }
                label="No Typical Signs & Symptoms"
              />
            </Flex>
          </Flex>
        </PageContent>
      </Page>

      <Page
        size="A4"
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 30,
        }}
      >
        <PageHeading pharmacyName="Pharmacy Name/Logo" />
        <PageContent>
          <Flex gap={16}>
            <H3>Red Flag Medical Conditions</H3>
            <Flex gap={8}>
              <CheckInput
                checked={
                  formValues.reasonsForReferral.redFlagMedicalConditions
                    .otitisMedia
                }
                label="Otitis Media"
              />
              <CheckInput
                checked={
                  formValues.reasonsForReferral.redFlagMedicalConditions
                    .chronicRecurrentSinusitis
                }
                label="Chronic, Recurrent Sinusitis"
              />
              <CheckInput
                checked={
                  formValues.reasonsForReferral.redFlagMedicalConditions
                    .uncontrolledThyroid
                }
                label="Uncontrolled Thyroid"
              />

              <CheckInput
                checked={
                  formValues.reasonsForReferral.redFlagMedicalConditions
                    .uncontrolledAsthma
                }
                label="Uncontrolled Asthma"
              />
            </Flex>
          </Flex>
          <Flex gap={16}>
            <H3>Drug Induced Allergic Rhinitis</H3>
            <Flex gap={12}>
              <H4>
                Recently started a new medication and coinciding Allergic Rhinitis symptoms:
              </H4>
              <Flex gap={8}>
                <Flex gap={4}>
                  <CheckInput
                    checked={
                      formValues.reasonsForReferral.drugInducedAllergicRhinitis
                        .prescriptionMedications
                    }
                    label="Prescription Medications"
                  />
                  <View style={{ paddingLeft: 22 }}>
                    <TextInput
                      value={
                        formValues.reasonsForReferral
                          .drugInducedAllergicRhinitis
                          .prescriptionMedicationsValue
                      }
                    />
                  </View>
                </Flex>
                <CheckInput
                  checked={
                    formValues.reasonsForReferral.drugInducedAllergicRhinitis
                      .oralContraceptives
                  }
                  label="Oral Contraceptives"
                />
                <CheckInput
                  checked={
                    formValues.reasonsForReferral.drugInducedAllergicRhinitis
                      .nonPrescriptionMedications
                  }
                  label="Non Prescription Medications"
                />
                <CheckInput
                  checked={
                    formValues.reasonsForReferral.drugInducedAllergicRhinitis
                      .herbalVitamins
                  }
                  label="Herbal / Vitamins"
                />
                <CheckInput
                  checked={
                    formValues.reasonsForReferral.drugInducedAllergicRhinitis
                      .sampleMedications
                  }
                  label="Sample Medications"
                />
                <CheckInput
                  checked={
                    formValues.reasonsForReferral.drugInducedAllergicRhinitis
                      .cocaine
                  }
                  label="Cocaine"
                />
              </Flex>
            </Flex>
          </Flex>
          <Flex gap={16}>
            <H3>Differential Assessment</H3>
            <Flex gap={8}>
              <Flex gap={4}>
                <Flex gap={0}>
                  <CheckInput
                    checked={
                      formValues.reasonsForReferral.differentialAssessment
                        .irritantRhinitis
                    }
                    label="Irritant (Non-Allergic) Rhinitis - exposure to a physical or chemical irritant"
                  />
                  <View style={{ paddingLeft: 22 }}>
                    <P>Name if Irritant if known:</P>
                  </View>
                </Flex>
                <View style={{ paddingLeft: 22 }}>
                  <TextInput
                    value={
                      formValues.reasonsForReferral.differentialAssessment
                        .irritantRhinitisValue
                    }
                  />
                </View>
              </Flex>
              <CheckInput
                checked={
                  formValues.reasonsForReferral.differentialAssessment
                    .occupationalRhinitis
                }
                label="Occupational Rhinitis"
              />
              <CheckInput
                checked={
                  formValues.reasonsForReferral.differentialAssessment
                    .structuralOrMechanical
                }
                label="Structural or Mechanical Factors"
              />
              <CheckInput
                checked={
                  formValues.reasonsForReferral.differentialAssessment
                    .systemicConditions
                }
                label="Systemic Conditions"
              />
            </Flex>
          </Flex>
          <Flex gap={16}>
            <H3>Interventions</H3>
            <Flex gap={8}>
              <CheckInput
                checked={
                  formValues.reasonsForReferral.interventions
                    .suggestedToDiscontinueMedication
                }
                label="Suggested to discontinue medication"
              />
              <CheckInput
                checked={
                  formValues.reasonsForReferral.interventions
                    .suggestedToChangeMedication
                }
                label="Suggested to change medication"
              />
            </Flex>
          </Flex>
        </PageContent>
      </Page>

      <Page
        size="A4"
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 30,
        }}
      >
        <PageHeading pharmacyName="Pharmacy Name/Logo" />
        <PageContent>
          <Flex gap={16}>
            <H2 filled>Prescribing Pharmacist Information</H2>
            <Flex gap={12}>
              <TextInput
                label="Pharmacist Name"
                value={formValues.prescribingPharmacistInformation.name}
              />
              <TextInput
                label="Pharmacist License Number"
                value={
                  formValues.prescribingPharmacistInformation.licenseNumber
                }
              />
              <TextAreaInput
                label="Pharmacist Signature"
                value={formValues.prescribingPharmacistInformation.signature}
                lines={2}
              />
            </Flex>
          </Flex>
          <Flex gap={16}>
            <H2 filled>Pharmacy Information</H2>
            <Flex gap={12}>
              <TextInput
                label="Pharmacy Name"
                value={formValues.pharmacyInformation.name}
              />
              <FlexRow gap={12}>
                <TextInput
                  label="Store Number"
                  value={formValues.pharmacyInformation.storeNumber}
                />
                <View style={{ minWidth: 255.5 }} />
              </FlexRow>
              <FlexRow gap={12}>
                <TextInput
                  label="Pharmacy Phone"
                  value={formValues.pharmacyInformation.phone}
                />
                <TextInput
                  label="Pharmacy Fax"
                  value={formValues.pharmacyInformation.fax}
                />
              </FlexRow>
              <TextInput
                label="Pharmacy Address"
                value={formValues.pharmacyInformation.address}
              />
            </Flex>
          </Flex>
        </PageContent>
      </Page>
    </Document>
  );
};
